import { SubmitHandler, useForm } from 'react-hook-form';

import { User } from 'interfaces/User';

import { Button, Input, PasswordInput, Switch } from '@components';
import MeAPICaller from '@services/api/me';
import UserAPICaller from '@services/api/user';
import { formEmailPattern } from '@validations/email';
import { useCallback } from 'react';

interface Props {
  closeModal: () => void;
  onSave: () => void;
  editId?: string;
  isMe?: boolean;
}

interface DefaultValue {
  id: string | null;
  name: string;
  active: boolean;
  email: string;
  password?: string;
  admin?: boolean;
  passwordConfirm?: string;
}

const { createOrUpdate, fetchUser } = UserAPICaller;
const { update, fetchMe } = MeAPICaller;

const fetchDefaultValues = async ({
  isMe,
  editId,
}: Pick<Props, 'editId' | 'isMe'>): Promise<DefaultValue> => {
  if (!editId) {
    return {
      id: null,
      active: true,
      name: '',
      email: '',
      password: '',
      admin: true,
    };
  }

  let fetchedUser: User | null = null;
  if (isMe) {
    fetchedUser = await fetchMe();
  } else {
    fetchedUser = await fetchUser(editId);
  }

  return {
    id: fetchedUser.id || null,
    active: fetchedUser.active,
    name: fetchedUser.name,
    email: fetchedUser.email,
    admin: fetchedUser.admin,
  };
};

const UserForm = ({ isMe, editId, closeModal, onSave, ...props }: Props) => {
  const {
    register,
    setError,
    handleSubmit,
    formState: { errors, isLoading, isSubmitting, defaultValues },
  } = useForm<DefaultValue>({
    defaultValues: async () => fetchDefaultValues({ isMe, editId }),
  });

  const handleOnSubmit = useCallback<SubmitHandler<DefaultValue>>(
    async (data) => {
      const { ...userData } = data;

      if (data.password !== data.passwordConfirm) {
        setError('passwordConfirm', {
          type: 'manual',
          message: 'As senhas não coincidem',
        });
        return;
      }

      if (isMe) {
        return update<typeof userData>(userData, setError).then((res) => {
          if (res.data.errors) return;
          closeModal();
          onSave();
        });
      }

      const createOrUpdateResponse = await createOrUpdate<typeof userData>(
        userData,
        setError
      );

      if (createOrUpdateResponse.data.errors) return;
      if (!defaultValues) {
        return;
      }

      closeModal();
      onSave();
    },
    [closeModal, defaultValues, isMe, onSave, setError]
  );

  return (
    <div className="container p-s-200">
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <form
        role="form"
        autoComplete="off"
        className="form-max-height"
        onSubmit={handleSubmit(handleOnSubmit)}
      >
        <div className="d-flex flex-direction-column mb-s-200">
          <div className="mb-s-200">
            <Input
              disabled={isLoading}
              label="Nome"
              error={!!errors.name}
              caption={errors.name?.message as string}
              placeholder="Digite o nome do usuário"
              form={register('name', { required: 'Obrigatório' })}
            />
          </div>
          <div className="mb-s-200">
            <Input
              disabled={isLoading || !!editId}
              label="E-mail"
              error={!!errors.email}
              caption={errors.email?.message as string}
              placeholder="Digite o e-mail do usuário"
              form={register('email', {
                required: 'Obrigatório',
                ...formEmailPattern,
              })}
            />
          </div>

          <div className="mb-s-200">
            <PasswordInput
              disabled={isLoading}
              label="Senha"
              error={!!errors.password}
              caption={errors.password?.message as string}
              placeholder="Digite a senha"
              form={register('password')}
              dataTestId="password"
            />
          </div>

          <div className="mb-s-200">
            <PasswordInput
              disabled={isLoading}
              form={register('passwordConfirm')}
              label="Confirmar senha"
              placeholder="Digite a senha novamente"
              error={!!errors.passwordConfirm}
              caption={errors.passwordConfirm?.message as string}
              dataTestId="confirm password"
            />
          </div>

          {!isMe && (
            <div className="mb-s-200">
              <Switch
                disabled={isLoading}
                label="Possui função de admin do sistema?"
                caption={'Sim'}
                error={errors.admin?.message as string}
                form={register('admin')}
              />
            </div>
          )}

          <div className="d-flex justify-end">
            <Button
              disabled={isLoading}
              isLoading={isSubmitting || isLoading}
              className="pl-s-900 pr-s-900"
              type="submit"
            >
              Salvar
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UserForm;
